
import React, { useState, useMemo } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { formatCurrency } from '../utils/formatters';
import ChartTabs from '@/components/ui/chart-tabs';

type ProfitIncreaseChartProps = {
  data: any[];
};

const ProfitIncreaseChart: React.FC<ProfitIncreaseChartProps> = ({ data }) => {
  const [view, setView] = useState<string>('cumulative');
  
  // Calculate cumulative data
  const cumulativeData = useMemo(() => {
    let runningTotal = 0;
    return data.map(item => {
      runningTotal += item.restaurant_profit_increase || 0;
      return {
        ...item,
        restaurant_profit_increase: runningTotal
      };
    });
  }, [data]);
  
  const chartData = view === 'cumulative' ? cumulativeData : data;
  
  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
        <div className="space-y-1">
          <CardTitle>Profit Increase</CardTitle>
          <CardDescription>Additional profit by using Pump'd vs UberEats over time</CardDescription>
        </div>
        <ChartTabs value={view} onValueChange={setView} className="h-7" />
      </CardHeader>
      <CardContent>
        <div className="h-[300px]">
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart
              data={chartData}
              margin={{ top: 10, right: 10, left: 40, bottom: 0 }}
            >
              <defs>
                <linearGradient id="colorProfit" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#3f92ff" stopOpacity={0.8} />
                  <stop offset="95%" stopColor="#3f92ff" stopOpacity={0} />
                </linearGradient>
              </defs>
              <XAxis 
                dataKey="date" 
                tickFormatter={(value) => {
                  const date = new Date(value);
                  return date.toLocaleDateString('en-NZ', { day: '2-digit', month: '2-digit', year: 'numeric' });
                }}
              />
              <YAxis tickFormatter={(value) => formatCurrency(value)} />
              <CartesianGrid strokeDasharray="3 3" />
              <Tooltip 
                formatter={(value) => [formatCurrency(value), view === 'cumulative' ? 'Total Profit Increase' : 'Daily Profit Increase']}
                labelFormatter={(label) => {
                  const date = new Date(label);
                  return date.toLocaleDateString('en-NZ', { day: '2-digit', month: '2-digit', year: 'numeric' });
                }}
              />
              <Area
                type="monotone"
                dataKey="restaurant_profit_increase"
                stroke="#3f92ff"
                fillOpacity={1}
                fill="url(#colorProfit)"
              />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      </CardContent>
    </Card>
  );
};

export default ProfitIncreaseChart;
